'use client'

import { motion } from 'framer-motion'
import { BackgroundBeams } from '../../../_components/_ui/background-beams'
import Link from 'next/link'

export default function APIPage() {
    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="relative z-10 container mx-auto px-4 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-5xl mx-auto"
                >
                    <div className="mb-8">
                        <Link href="/support/documentation" className="text-indigo-400 hover:text-indigo-300 text-sm">
                            ← Back to Documentation
                        </Link>
                    </div>

                    <h1 className="text-4xl font-bold text-white mb-6">
                        API Documentation
                    </h1>
                    
                    <div className="prose prose-lg prose-invert max-w-none">
                        <p className="text-gray-300 text-lg mb-8">
                            Autofocus provides REST API endpoints for content delivery, integration, and automation. 
                            Access user profiles, pages, and blocks programmatically for custom applications and 
                            external integrations.
                        </p>

                        {/* API Overview */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🌐 API Overview
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-2">Base URL</h3>
                                    <div className="font-mono text-sm bg-neutral-800 p-3 rounded text-gray-300">
                                        https://autofoc.us/api
                                    </div>
                                    <p className="text-gray-400 text-sm mt-2">
                                        All API endpoints are accessed via this base URL with RESTful patterns.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-400 mb-2">Response Format</h3>
                                    <div className="font-mono text-sm bg-neutral-800 p-3 rounded text-gray-300">
                                        Content-Type: application/json
                                    </div>
                                    <p className="text-gray-400 text-sm mt-2">
                                        All responses are returned in JSON format with consistent error handling.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Content Delivery API */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                📦 Content Delivery API
                            </h2>
                            
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-purple-400 mb-3">Get User Profile</h3>
                                    <div className="bg-neutral-800 p-4 rounded mb-3">
                                        <div className="font-mono text-sm text-green-400 mb-2">GET /api/user/[username]</div>
                                        <p className="text-gray-300 text-sm">Retrieve public profile information for a user.</p>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Example Request</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`curl https://autofoc.us/api/user/nate
  -H "Accept: application/json"`}
                                            </pre>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Example Response</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`{
  "id": "uuid",
  "username": "nate",
  "display_name": "Nate",
  "bio": "Creator bio text",
  "avatar_url": "https://...",
  "social_links": {...},
  "created_at": "2024-01-01T00:00:00Z"
}`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-cyan-400 mb-3">Get Page Content</h3>
                                    <div className="bg-neutral-800 p-4 rounded mb-3">
                                        <div className="font-mono text-sm text-green-400 mb-2">GET /[username]/[page]?api=1</div>
                                        <p className="text-gray-300 text-sm">Retrieve all blocks for a specific page in JSON format.</p>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Example Request</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`curl https://autofoc.us/nate/portfolio?api=1
  -H "Accept: application/json"`}
                                            </pre>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Example Response</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`[
  {
    "id": "uuid",
    "type": "markdown",
    "content": {
      "text": "# Welcome to my page"
    },
    "order_index": 0,
    "created_at": "2024-01-01T00:00:00Z"
  },
  {
    "id": "uuid", 
    "type": "image",
    "content": {
      "url": "https://...",
      "alt": "Photo description"
    },
    "order_index": 1
  }
]`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Authentication API */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🔐 Authentication & Verification
                            </h2>
                            
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-red-400 mb-3">Verify Redirect Password</h3>
                                    <div className="bg-neutral-800 p-4 rounded mb-3">
                                        <div className="font-mono text-sm text-green-400 mb-2">POST /api/verify-redirect-password</div>
                                        <p className="text-gray-300 text-sm">Verify password for protected redirect pages.</p>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Request Body</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`{
  "username": "nate",
  "pageSlug": "private-page",
  "password": "secret123"
}`}
                                            </pre>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Response</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`{
  "success": true
}

// Or on failure:
{
  "success": false,
  "error": "Invalid password"
}`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Block Types API Reference */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🧩 Block Types API Reference
                            </h2>
                            <p className="text-gray-300 mb-6">
                                Each block type has a consistent structure but different content schemas. Here are the main block types and their API formats:
                            </p>
                            
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <h4 className="font-semibold text-purple-400 mb-2">Text/Markdown Block</h4>
                                        <pre className="text-xs text-gray-300 overflow-x-auto">
{`{
  "type": "markdown",
  "content": {
    "text": "# Heading\\n\\nParagraph text"
  }
}`}
                                        </pre>
                                    </div>
                                    
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <h4 className="font-semibold text-blue-400 mb-2">Image Block</h4>
                                        <pre className="text-xs text-gray-300 overflow-x-auto">
{`{
  "type": "image",
  "content": {
    "url": "https://example.com/image.jpg",
    "alt": "Image description",
    "caption": "Optional caption"
  }
}`}
                                        </pre>
                                    </div>
                                    
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <h4 className="font-semibold text-green-400 mb-2">Link Block</h4>
                                        <pre className="text-xs text-gray-300 overflow-x-auto">
{`{
  "type": "link",
  "content": {
    "url": "https://example.com",
    "title": "Link Title",
    "description": "Description text"
  }
}`}
                                        </pre>
                                    </div>
                                    
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <h4 className="font-semibold text-yellow-400 mb-2">Checklist Block</h4>
                                        <pre className="text-xs text-gray-300 overflow-x-auto">
{`{
  "type": "checklist",
  "content": {
    "items": [
      {"text": "Task 1", "completed": false},
      {"text": "Task 2", "completed": true}
    ]
  }
}`}
                                        </pre>
                                    </div>
                                </div>
                                
                                <div className="space-y-4">
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <h4 className="font-semibold text-indigo-400 mb-2">Code Block</h4>
                                        <pre className="text-xs text-gray-300 overflow-x-auto">
{`{
  "type": "code",
  "content": {
    "code": "console.log('Hello');",
    "language": "javascript",
    "theme": "dark"
  }
}`}
                                        </pre>
                                    </div>
                                    
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <h4 className="font-semibold text-orange-400 mb-2">Audio Block</h4>
                                        <pre className="text-xs text-gray-300 overflow-x-auto">
{`{
  "type": "audio",
  "content": {
    "url": "https://example.com/audio.mp3",
    "title": "Audio Title",
    "duration": 180
  }
}`}
                                        </pre>
                                    </div>
                                    
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <h4 className="font-semibold text-pink-400 mb-2">Location Block</h4>
                                        <pre className="text-xs text-gray-300 overflow-x-auto">
{`{
  "type": "location",
  "content": {
    "latitude": 40.7128,
    "longitude": -74.0060,
    "title": "Location Name",
    "description": "Details"
  }
}`}
                                        </pre>
                                    </div>
                                    
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <h4 className="font-semibold text-cyan-400 mb-2">Kanban Block</h4>
                                        <pre className="text-xs text-gray-300 overflow-x-auto">
{`{
  "type": "kanban",
  "content": {
    "columns": [
      {
        "title": "To Do",
        "cards": [
          {"title": "Task", "description": "Details"}
        ]
      }
    ]
  }
}`}
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Error Handling */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                ⚠️ Error Handling & Status Codes
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-red-400 mb-3">HTTP Status Codes</h3>
                                    <div className="space-y-2 text-sm">
                                        <div className="bg-neutral-800 p-2 rounded flex justify-between">
                                            <span className="text-green-400 font-mono">200</span>
                                            <span className="text-gray-300">Success</span>
                                        </div>
                                        <div className="bg-neutral-800 p-2 rounded flex justify-between">
                                            <span className="text-yellow-400 font-mono">400</span>
                                            <span className="text-gray-300">Bad Request</span>
                                        </div>
                                        <div className="bg-neutral-800 p-2 rounded flex justify-between">
                                            <span className="text-orange-400 font-mono">404</span>
                                            <span className="text-gray-300">Not Found</span>
                                        </div>
                                        <div className="bg-neutral-800 p-2 rounded flex justify-between">
                                            <span className="text-red-400 font-mono">500</span>
                                            <span className="text-gray-300">Server Error</span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">Error Response Format</h3>
                                    <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`{
  "error": "Profile not found",
  "code": "PROFILE_NOT_FOUND",
  "message": "The requested user profile does not exist.",
  "timestamp": "2024-01-01T00:00:00Z"
}`}
                                    </pre>
                                </div>
                            </div>
                        </div>

                        {/* Rate Limiting */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🚦 Rate Limiting & Best Practices
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-yellow-400 mb-3">Rate Limits</h3>
                                    <div className="space-y-2 text-sm text-gray-300">
                                        <div>• Public API: 100 requests per minute</div>
                                        <div>• Authenticated API: 1000 requests per minute</div>
                                        <div>• Bulk operations: 10 requests per minute</div>
                                        <div>• Rate limit headers included in responses</div>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-3">Best Practices</h3>
                                    <div className="space-y-2 text-sm text-gray-300">
                                        <div>• Cache responses when possible</div>
                                        <div>• Use pagination for large datasets</div>
                                        <div>• Implement exponential backoff for retries</div>
                                        <div>• Monitor rate limit headers</div>
                                        <div>• Use webhook subscriptions for real-time updates</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Integration Examples */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🔌 Integration Examples
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-400 mb-3">JavaScript/Node.js</h3>
                                    <pre className="bg-neutral-800 p-4 rounded text-sm text-gray-300 overflow-x-auto">
{`// Fetch user profile
const response = await fetch('https://autofoc.us/api/user/nate');
const profile = await response.json();

// Get page blocks
const pageResponse = await fetch('https://autofoc.us/nate/portfolio?api=1');
const blocks = await pageResponse.json();

// Filter for specific block types
const imageBlocks = blocks.filter(block => block.type === 'image');
const textBlocks = blocks.filter(block => block.type === 'markdown');`}
                                    </pre>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-purple-400 mb-3">Python</h3>
                                    <pre className="bg-neutral-800 p-4 rounded text-sm text-gray-300 overflow-x-auto">
{`import requests

# Get user profile
response = requests.get('https://autofoc.us/api/user/nate')
profile = response.json()

# Get page content
page_response = requests.get('https://autofoc.us/nate/portfolio?api=1')
blocks = page_response.json()

# Process blocks by type
for block in blocks:
    if block['type'] == 'image':
        print(f"Image: {block['content']['url']}")
    elif block['type'] == 'markdown':
        print(f"Text: {block['content']['text'][:100]}...")`}
                                    </pre>
                                </div>
                            </div>
                        </div>

                        {/* Next Steps */}
                        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">
                                🚀 Explore More
                            </h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <Link href="/support/documentation/blocks" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-purple-400 mb-1">Block System →</div>
                                    <div className="text-gray-300 text-sm">Deep dive into how content blocks work</div>
                                </Link>
                                <Link href="/support/documentation/auth" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-red-400 mb-1">Authentication →</div>
                                    <div className="text-gray-300 text-sm">Learn about user roles and permissions</div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}